/**
 * Admin Command Hub - Main landing page after admin login
 * Shows 10 main navigation cards with preview stats
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  GraduationCap, Layers, Shield, TrendingUp, DollarSign, BarChart3,
  Crown, LogOut, ArrowRight, Package, LineChart, FolderOpen, MessageCircle, Users, Activity, Brain, ClipboardList
} from 'lucide-react';

const ADMIN_CARDS = [
  { 
    id: 'clients', 
    label: 'Client Profiles', 
    icon: Users, 
    path: '/admin/clients',
    color: '#D4AF37',
    gradient: 'from-[#D4AF37]/20 to-[#D4AF37]/5',
    description: 'Unified client management with services, documents, and metrics',
    features: ['Client Database', 'Service Tracking', 'Document Vault', 'Activity Log'],
    previewStat: 'Unified Management'
  },
  { 
    id: 'messages', 
    label: 'Messages', 
    icon: MessageCircle, 
    path: '/admin/messages',
    color: '#06B6D4',
    gradient: 'from-[#06B6D4]/20 to-[#06B6D4]/5',
    description: 'Real-time chat with carriers, load offers, and document signing',
    features: ['Instant Messaging', 'Load Offers', 'Document Signing', 'Push Notifications'],
    previewStat: '3 Unread Messages'
  },
  { 
    id: 'academy', 
    label: 'Academy', 
    icon: GraduationCap, 
    path: '/admin/academy',
    color: '#D4AF37',
    gradient: 'from-[#D4AF37]/20 to-[#D4AF37]/5',
    description: 'Manage courses, digital products, training modules, and downloadables',
    features: ['Course Management', 'Access Codes', 'Enrollments', 'Revenue Tracking'],
    previewStat: '156 Active Students'
  },
  { 
    id: 'resources', 
    label: 'Resources', 
    icon: Package, 
    path: '/admin/resources',
    color: '#EC4899',
    gradient: 'from-[#EC4899]/20 to-[#EC4899]/5',
    description: 'Manage template packets, digital downloads, and product inventory',
    features: ['Template Packets', 'Product Management', 'Purchase History', 'Download Tracking'],
    previewStat: '47 Total Sales'
  },
  { 
    id: 'documents', 
    label: 'E-Sign Center', 
    icon: FolderOpen, 
    path: '/admin/documents',
    color: '#6366F1',
    gradient: 'from-[#6366F1]/20 to-[#6366F1]/5',
    description: 'E-signature management, compliance documents, and client signing status',
    features: ['E-Signatures', 'Document Templates', 'Signing Status', 'PDF Downloads'],
    previewStat: 'Compliance Hub'
  },
  { 
    id: 'applications', 
    label: 'Access Control', 
    icon: Users, 
    path: '/admin/applications',
    color: '#14B8A6',
    gradient: 'from-[#14B8A6]/20 to-[#14B8A6]/5',
    description: 'Manage applications, access codes, magic links, and user authentication',
    features: ['Application Review', 'Access Codes', 'Magic Links', 'Login Activity'],
    previewStat: 'Auth System'
  },
  {
    id: 'intake',
    label: 'Carrier Applications',
    icon: ClipboardList,
    path: '/admin/intake',
    color: '#D4AF37',
    gradient: 'from-[#D4AF37]/20 to-[#D4AF37]/5',
    description: 'Review public intake submissions from the Join The Hive form',
    features: ['Intake Submissions', 'Drop-off Tracking', 'Stage Routing', 'Promote to Application'],
    previewStat: 'New Leads'
  },
  { 
    id: 'build', 
    label: 'Build', 
    icon: Layers, 
    path: '/admin/build',
    color: '#8B5CF6',
    gradient: 'from-[#8B5CF6]/20 to-[#8B5CF6]/5',
    description: 'Track new carriers, authority setup, starter packs, and onboarding',
    features: ['New Applications', 'Authority Setup', 'Signature Program Clients', 'Onboarding Progress'],
    previewStat: '47 New Carriers'
  },
  { 
    id: 'protect', 
    label: 'Protect', 
    icon: Shield, 
    path: '/admin/protect',
    color: '#10B981',
    gradient: 'from-[#10B981]/20 to-[#10B981]/5',
    description: 'Monitor active carriers, dispatch operations, and compliance',
    features: ['Active Carriers', 'Compliance Tracking', 'Dispatch Operations', 'Load Management'],
    previewStat: '156 Active Loads'
  },
  { 
    id: 'grow', 
    label: 'Grow', 
    icon: TrendingUp, 
    path: '/admin/grow',
    color: '#F59E0B',
    gradient: 'from-[#F59E0B]/20 to-[#F59E0B]/5',
    description: 'Manage scaling carriers, multi-truck fleets, and expansion tools',
    features: ['Scaling Carriers', 'Multi-Truck Fleets', 'Advanced Tools', 'Expansion Tracking'],
    previewStat: '147 Trucks Scaling'
  },
  { 
    id: 'finance', 
    label: 'Finance', 
    icon: DollarSign, 
    path: '/admin/finance',
    color: '#22C55E',
    gradient: 'from-[#22C55E]/20 to-[#22C55E]/5',
    description: 'Track revenue, subscriptions, service breakdowns, and payouts',
    features: ['Revenue Dashboard', 'Subscription Payments', 'Service Breakdown', 'Payout Visibility'],
    previewStat: '$42,350 This Week'
  },
  { 
    id: 'analytics', 
    label: 'Analytics', 
    icon: BarChart3, 
    path: '/admin/analytics',
    color: '#3B82F6',
    gradient: 'from-[#3B82F6]/20 to-[#3B82F6]/5',
    description: 'View KPI dashboards, performance metrics, and detailed reports',
    features: ['KPI Dashboards', 'Performance Metrics', 'Weekly/Monthly Reports', 'Carrier Insights'],
    previewStat: '92% Performance'
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: Activity,
    path: '/admin/insights',
    color: '#D4AF37',
    gradient: 'from-[#D4AF37]/20 to-[#D4AF37]/5',
    description: 'MRR, churn, ARPU, admin audit trail and accountability',
    features: ['MRR Dashboard', 'Churn Rate', 'ARPU', 'Admin Audit Log'],
    previewStat: 'Live Revenue Health'
  },
  {
    id: 'intelligence',
    label: 'Hive Intelligence',
    icon: Brain,
    path: '/admin/intelligence',
    color: '#EC4899',
    gradient: 'from-[#EC4899]/20 to-[#EC4899]/5',
    description: 'AI freight intelligence — market trends, load analysis, dispatch assistant',
    features: ['Market Overview', 'Load Analysis', 'Lane Intel', 'AI Assistant'],
    previewStat: 'Powered by Claude 4.5'
  },
  { 
    id: 'google-analytics', 
    label: 'Google Analytics', 
    icon: LineChart, 
    path: '/admin/google-analytics',
    color: '#EA4335',
    gradient: 'from-[#EA4335]/20 to-[#EA4335]/5',
    description: 'Monitor website traffic, page views, user behavior, and conversions',
    features: ['Page Views', 'User Sessions', 'Traffic Sources', 'Conversion Tracking'],
    previewStat: '12,847 Page Views'
  }
];

const AdminCommandHub = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check auth
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }
  }, [navigate]);

  const handleLogout = async () => {
    try {
      // Call backend logout to clear cookies
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear ALL localStorage auth tokens
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('hive_token');
    localStorage.removeItem('hive_user');
    // Force navigation to login
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center gold-glow-hover transition-all duration-300">
                <Crown className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-white font-bold text-lg tracking-tight group-hover:text-[#D4AF37] transition-colors">Queen B Logistics</h1>
                <p className="text-white/40 text-[10px] uppercase tracking-[0.2em]">Command Center</p>
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {user && (
              <div className="text-right hidden sm:block">
                <p className="text-white text-sm font-medium">{user.name || user.email}</p>
                <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">Administrator</p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all duration-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Welcome Section */}
        <div className="text-center mb-12 sm:mb-16 fade-in-up">
          <div className="limited-badge mx-auto mb-6">
            <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
            Limited Access Platform
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4 tracking-tight">
            COMMAND <span className="text-[#D4AF37]">HUB</span>
          </h1>
          <p className="text-white/50 text-base sm:text-lg max-w-2xl mx-auto">
            Welcome to the Queen B Logistics Admin Control Center. Select a section to manage your operations.
          </p>
        </div>

        {/* Navigation Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {ADMIN_CARDS.map((card, index) => {
            const Icon = card.icon;
            
            return (
              <Link
                key={card.id}
                to={card.path}
                className={`
                  group relative card-dark
                  p-5 sm:p-6 
                  fade-in-up stagger-${(index % 5) + 1}
                `}
                data-testid={`card-${card.id}`}
              >
                {/* Background Gradient */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `linear-gradient(135deg, ${card.color}10 0%, transparent 60%)` }}
                />
                
                {/* Preview Stat Badge */}
                {card.previewStat && (
                  <div 
                    className="absolute top-4 right-4 px-3 py-1.5 text-xs font-bold tracking-wide"
                    style={{ backgroundColor: `${card.color}15`, color: card.color, border: `1px solid ${card.color}30` }}
                  >
                    {card.previewStat}
                  </div>
                )}

                {/* Icon */}
                <div 
                  className="relative w-14 h-14 flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${card.color}15`, border: `1px solid ${card.color}30` }}
                >
                  <Icon className="w-7 h-7" style={{ color: card.color }} />
                  {/* Icon Glow */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"
                    style={{ backgroundColor: card.color }}
                  />
                </div>

                {/* Title */}
                <h2 className="relative text-2xl font-black text-white mb-2 flex items-center gap-3 tracking-tight uppercase">
                  {card.label}
                  <ArrowRight 
                    className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] transition-all duration-300 group-hover:translate-x-2" 
                  />
                </h2>

                {/* Description */}
                <p className="relative text-white/50 text-sm mb-5 leading-relaxed">
                  {card.description}
                </p>

                {/* Features */}
                <div className="relative flex flex-wrap gap-2">
                  {card.features.slice(0, 3).map((feature, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-1 text-[10px] uppercase tracking-wider bg-white/5 text-white/40 border border-white/5 group-hover:border-white/10 transition-colors"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Hover Accent Line */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                  style={{ backgroundColor: card.color }}
                />
              </Link>
            );
          })}
        </div>

        {/* Quick Stats Footer */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <p className="text-center text-white/20 text-xs uppercase tracking-[0.3em]">
            Queen B Logistics • Build. Protect. Grow. • Admin Control Center v2.0
          </p>
        </div>
      </main>
    </div>
  );
};

export default AdminCommandHub;
